import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
  Card,
  CardContent,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CoreButtonIcon from "../../../app/core/components/CoreButtonIcon/CoreButtonIcon";
import CoreSpinner from "../../../app/core/components/CoreSpinner/CoreSpinner";
import { formattaDataOraIta } from "../../../utils/functions/date-utils";
import { hexToRgba } from "../../../utils/functions/hexToRgba";
import {
  useDoDeleteMessageChatMutation,
  useDoEditMessageChatMutation,
  useDoSendingChatMutation,
  useGetChatConversationByIdQuery,
} from "../../api/admin/assistenteSmartApi";
import { useCreateChatbotMutation } from "../../api/admin/chatbotApi";
import { t } from "i18next";
import {
  useDoSendleChatMutation,
  useGetUserChatConversationByIdQuery,
} from "../../api/user/assistenteSmartApi";
import { ROLE } from "../../../utils/constants/profiles";
import CoreMaxCharacter from "../../../app/core/components/CoreMaxCharacter/CoreMaxCharacter";
import { convertTextToEmoji } from "../../../utils/constants/emojiMap";

const ChatBox = ({ chatId, status }) => {
  const role = useSelector((state) => state.app.role);
  const colorPrimary = useSelector((state) => state.app.primaryColor);
  const rgbaColor = hexToRgba(colorPrimary, 0.3);
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([]);
  const [idChat, setIdChat] = useState(chatId);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedMessageUser, setSelectMessageUser] = useState(null);
  const [editText, setEditText] = useState("");
  const [idMsg, setIdMsg] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const maxCharacter = 2000;

  const {
    data: chatAdmin,
    isLoading: isLoadingAdmin,
    refetch: refetchAdmin,
  } = useGetChatConversationByIdQuery(idChat, { skip: role === ROLE.USER });
  const {
    data: chatUser,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useGetUserChatConversationByIdQuery(idChat, { skip: role !== ROLE.USER });

  // User
  const [doSendleChat, { isLoading: isLoadingChatUser }] =
    useDoSendleChatMutation();
  // Admin
  const [doSendingChat, { isLoading: isLoadingChatAdmin }] =
    useDoSendingChatMutation();
  const [doEditMessageChat] = useDoEditMessageChatMutation();
  const [doDeleteMessageChat] = useDoDeleteMessageChatMutation();
  const [doSendingChatbot] = useCreateChatbotMutation();

  const isLoadingChat = isLoadingChatUser || isLoadingChatAdmin;

  useEffect(() => {
    setIdChat(chatId);
    setText("");
  }, [chatId]);

  useEffect(() => {
    if (role !== ROLE.USER && chatAdmin && chatAdmin.Data)
      setMessages(chatAdmin.Data);
    if (role === ROLE.USER && chatUser && chatUser.Data)
      setMessages(chatUser.Data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatAdmin, chatUser]);

  useEffect(() => {
    if (status === "Open") {
      const intervalId = setInterval(() => {
        if (role !== ROLE.USER) refetchAdmin();
        if (role === ROLE.USER) refetchUser();
      }, 5000); // Ogni 5 secondi

      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idChat]);

  useEffect(() => {
    setMessages((prevMessages) => {
      return prevMessages.map((el) => ({
        ...el,
        icon: el.id === selectedMessageId,
      }));
    });
  }, [selectedMessageId]);

  useEffect(() => {
    // Scrolla verso il basso quando i messaggi cambiano
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight; // Scroll immediato
    }
  }, [messages]);

  const handleSendMessage = () => {
    const bodyData = {
      id: idChat,
      message: text,
    };
    if (role !== ROLE.USER) doSendingChat(bodyData);
    if (role === ROLE.USER) doSendleChat(bodyData, bodyData.message);
    setText("");
  };

  const handleSendEditMessage = () => {
    const bodyData = {
      conversationId: idChat,
      messageId: idMsg,
      message: editText,
    };
    doEditMessageChat(bodyData);
    setEditText("");
    setSelectedMessageId(null);
  };

  const handleToogle = (messageId) => {
    if (selectedMessageId === messageId) {
      setSelectedMessageId(null);
      setEditText("");
      return;
    }
    setSelectedMessageId(messageId);
  };

  const handleToogleUser = (el) => {
    if (selectedMessageUser === el) {
      return setSelectMessageUser(null);
    }
    setSelectMessageUser(el);
  };

  const handleDeleteMessage = (Id) => {
    const msg = toast.loading(t("Deleting..."), {
      type: "info",
    });
    const bodyData = {
      conversationId: idChat,
      messageId: Id,
    };
    doDeleteMessageChat(bodyData, bodyData.messageId).then((res) => {
      if (res.data.Result !== 1) {
        toast.update(msg, {
          render: t("SingleMessageNotDeleted"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
        return;
      }
      toast.update(msg, {
        render: t("SingleMessageDeleted"),
        type: "success",
        autoClose: 1500,
        isLoading: false,
      });
    });
    setEditText("");
    setSelectedMessageId(null);
  };

  const handleEditMessage = (el) => {
    setText("");
    setIdMsg(el.id);
    setEditText(el.message);
  };

  const handleChangeText = (e) => {
    setText(e.target.value);
  };

  const handleChangeEditText = (e) => {
    setEditText(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!selectedMessageId) {
        handleSendMessage();
      } else {
        handleSendEditMessage();
      }
    }
  };

  const handleResponseIA = (el) => {
    const msg = toast.loading(t("GeneratingMessage..."), {
      type: "info",
    });
    const userMessage = {
      role: "user",
      content: el.message,
      timestamp: null,
    };

    const bodyData = {
      history: [userMessage],
      systemPrompt:
        "Tu sei un esperto in materia fiscale e mi devi rispondere solo su domande in ambito fiscale",
      newMessage: el.message,
    };

    doSendingChatbot(bodyData).then((res) => {
      if (res?.data?.message) {
        setText(res?.data?.message?.content);
        toast.update(msg, {
          render: t("SingleMessageGenerated"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
      } else {
        toast.update(msg, {
          render: t("SingleMessageNotGenerated"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      }
    });
  };

  const handleViewChat = (msg) => {
    let message;
    if (role !== ROLE.USER) message = msg.sender === "User";
    if (role === ROLE.USER) message = msg.sender !== "User";

    return message;
  };

  return (
    <div className=" h-full">
      <div
        className="overflow-auto "
        style={{ height: "580px" }}
        ref={messagesContainerRef}
      >
        <div className="flex w-full h-full flex-col">
          {isLoadingAdmin || isLoadingUser ? (
            <CoreSpinner className="flex w-full h-full justify-center items-center" />
          ) : (
            <div className="flex flex-col w-full items-start p-2">
              {[...messages]
                .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .map((el) => {
                  return handleViewChat(el) ? (
                    <div key={el.id}>
                      <div className="flex items-center">
                        {role !== ROLE.USER && (
                          <>
                            <div className="flex items-center ">
                              <IconButton
                                disabled={status === "Archived" ? true : false}
                                onClick={() => handleToogleUser(el.id)}
                              >
                                <MoreVertRoundedIcon />
                              </IconButton>
                            </div>
                            {selectedMessageUser === el.id && (
                              <Tooltip title={t("AnswerWithIA")}>
                                <IconButton
                                  onClick={() => handleResponseIA(el)}
                                >
                                  <SmartToyOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                        <div className="flex flex-col">
                          <Card
                            style={{
                              borderRadius: "20px",
                              background: "white ",
                            }}
                            component={Paper}
                            elevation={1}
                            className="m-1"
                          >
                            <CardContent>
                              <p className="text-black normal-case m-0 break-all">
                                {convertTextToEmoji(el.message)}
                              </p>
                            </CardContent>
                            {role === ROLE.USER && (
                              <Typography
                                variant="caption"
                                color={colorPrimary}
                                className="px-4"
                              >
                                {el.username}
                              </Typography>
                            )}
                          </Card>
                          <small className="text-xs mx-4">
                            {formattaDataOraIta(el.createdAt)}
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex flex-col w-full items-end p-2"
                      key={el.id}
                    >
                      <div className="flex mx-4 ">
                        <div>
                          <Card
                            style={{
                              borderRadius: "20px",
                              backgroundColor: `${rgbaColor}`,
                            }}
                            component={Paper}
                            elevation={1}
                            className="m-1"
                          >
                            <CardContent className="justify-end">
                              <p className="text-black normal-case m-0 break-all">
                                {convertTextToEmoji(el.message)}
                              </p>
                            </CardContent>
                            {role !== ROLE.USER && (
                              <Typography
                                variant="caption"
                                color={colorPrimary}
                                className="px-4"
                              >
                                {el.username}
                              </Typography>
                            )}
                          </Card>
                          <small className="text-xs flex justify-end mx-4">
                            {formattaDataOraIta(el.createdAt)}
                          </small>
                        </div>
                        {role !== ROLE.USER && (
                          <>
                            <div className="flex items-center">
                              <IconButton
                                disabled={status === "Archived" ? true : false}
                                onClick={() => handleToogle(el.id)}
                              >
                                <MoreVertRoundedIcon />
                              </IconButton>
                            </div>

                            {status === "Open" &&
                              selectedMessageId === el.id && (
                                <div className="flex items-center m-1">
                                  <Tooltip title={t("Edit")}>
                                    <IconButton
                                      onClick={() => handleEditMessage(el)}
                                    >
                                      <EditRoundedIcon className="text-zinc-500 " />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("Delete")}>
                                    <IconButton
                                      onClick={() => handleDeleteMessage(el.id)}
                                    >
                                      <DeleteRoundedIcon className="text-zinc-500 " />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              <div ref={messagesEndRef} />{" "}
            </div>
          )}
        </div>
      </div>

      {status === "Open" && (
        <div className="flex justify-center items-center">
          <div className="w-full">
            <Divider />

            <div className="flex h-60 justify-center items-center">
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: "95%",
                  borderRadius: "16px",
                  border: "solid 1px lightgrey",
                }}
                elevation={0}
                className={editText ? "bg-orange-100" : null}
              >
                <InputBase
                  multiline
                  minRows={4}
                  maxRows={9}
                  sx={{ ml: 1, flex: 1 }}
                  placeholder={t("WriteAMessage")}
                  value={text || editText}
                  onChange={!editText ? handleChangeText : handleChangeEditText}
                  onKeyDown={handleKeyDown}
                  className={
                    (text.length || editText.length) > maxCharacter &&
                    "text-red-500"
                  }
                />

                <CoreMaxCharacter
                  maxCharacter={maxCharacter}
                  text={text}
                  editText={editText}
                />

                <Divider orientation="vertical" />
                <CoreButtonIcon
                  disabled={
                    isLoadingChat ||
                    (!text && !editText) ||
                    (text.length || editText.length) > maxCharacter
                  }
                  icon={<SendRoundedIcon />}
                  onClick={
                    !editText
                      ? () => handleSendMessage()
                      : () => handleSendEditMessage()
                  }
                />
              </Paper>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBox;
